import { useInView } from '../../../hooks/useInView';

const companyTasks = [
  'スカウトへの返信・面談調整',
  '選考・合否の判断',
  '自社の採用情報の提供',
];

const agencyTasks = [
  '採用ターゲットの設計・定義',
  '媒体内での学生検索・選定',
  'スカウト文面の作成・カスタマイズ',
  'スカウト送信・送信管理',
  '返信率・承認率のデータ分析',
  'PDCAに基づく継続改善',
];

const platforms = [
  {
    name: 'OfferBox',
    desc: '国内最大級の逆求人型就活サービス',
    logo: 'https://offerbox.jp/ofbstudent/assets/img/logo.png',
    logoBg: '#ffffff',
    url: 'https://offerbox.jp/company/',
    recommended: true,
  },
  {
    name: 'dodaキャンパス',
    desc: 'ベネッセ運営の逆求人プラットフォーム',
    logo: 'https://campus.doda.jp/assets/images/logo/logo_doda_2018.svg',
    logoBg: '#ffffff',
    url: 'https://campus.doda.jp/enterprise/',
    recommended: true,
  },
];

export default function ServiceSection() {
  const { ref, inView } = useInView();

  return (
    <section id="service" className="py-10 md:py-20 bg-white">
      <div ref={ref} className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className={`text-center mb-8 md:mb-14 transition-all duration-700 ease-out ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="text-xs tracking-widest text-[#E8601C] font-medium uppercase mb-3 block">
            SERVICE
          </span>
          <h2
            className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1a2e4a] leading-tight"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            新卒スカウト送信を、<br />戦略的に代行します。
          </h2>
          <p className="text-[#666] text-sm mt-4 max-w-xl mx-auto leading-relaxed">
            単純な「送信作業」ではなく、ターゲット設計からデータ改善まで一貫して支援する
            採用成果につながる仕組みを提供します。
          </p>
        </div>

        {/* Main Content: Image + Text */}
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-12 items-center mb-8 sm:mb-14 transition-all duration-700 ease-out ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: inView ? '200ms' : '0ms' }}>
          {/* Visual */}
          <div className="relative pb-6 sm:pb-0">
            <div className="rounded-2xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/fbb1e90b69160921b9ac3f47076ec233.jpg"
                alt="スカウト代行サービスのダッシュボードイメージ"
                className="w-full h-56 sm:h-72 object-cover object-top"
              />
            </div>
            {/* Floating Badge */}
            <div className="absolute bottom-2 right-2 sm:-bottom-4 sm:-right-4 bg-[#1a2e4a] text-white rounded-xl px-4 sm:px-5 py-2.5 sm:py-3">
              <p className="text-xs text-white/60 mb-0.5">平均返信率</p>
              <p className="text-xl sm:text-2xl font-bold" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                ５<span className="text-sm font-normal">%</span>
              </p>
            </div>
          </div>

          {/* Text */}
          <div>
            {/* Platforms */}
            <div className="mb-8">
              <p className="text-xs text-[#999] uppercase tracking-widest font-medium mb-3">対応媒体</p>
              <div className="flex gap-3 sm:gap-4 flex-wrap">
                {platforms.map((p) => (
                  <div key={p.name} className="relative">
                    {p.recommended && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10 whitespace-nowrap">
                        <span className="bg-[#E8601C] text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full tracking-wide">
                          ＼おすすめ／
                        </span>
                      </div>
                    )}
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex flex-col items-center justify-center gap-2 bg-white border border-[#e8e4de] rounded-xl px-4 sm:px-5 py-3 sm:py-4 min-w-[130px] sm:min-w-[140px] hover:border-[#1a2e4a]/40 hover:bg-[#f7f5f2] transition-all duration-200 cursor-pointer"
                    >
                      <div className="w-full h-8 flex items-center justify-center">
                        <img
                          src={p.logo}
                          alt={p.name}
                          className="h-7 w-auto object-contain"
                        />
                      </div>
                      <span className="text-xs text-[#888] leading-snug text-center">{p.desc}</span>
                      <span className="text-xs text-[#E8601C] flex items-center gap-1">
                        <i className="ri-external-link-line text-xs" />
                        企業向けページ
                      </span>
                    </a>
                  </div>
                ))}
              </div>
            </div>

            {/* What We Offer */}
            <div>
              <p className="text-xs text-[#999] uppercase tracking-widest font-medium mb-3">提供内容</p>
              <ul className="space-y-2">
                {agencyTasks.map((task) => (
                  <li key={task} className="flex items-start gap-2.5">
                    <i className="ri-arrow-right-s-line text-[#E8601C] text-base mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-[#444] leading-relaxed">{task}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Role Division */}
        <div className={`bg-[#f7f5f2] rounded-2xl p-6 sm:p-8 transition-all duration-700 ease-out ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: inView ? '400ms' : '0ms' }}>
          <p
            className="text-center text-base font-bold text-[#1a2e4a] mb-6 sm:mb-8"
            style={{ fontFamily: "'Noto Serif JP', serif" }}
          >
            役割分担のイメージ
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
            {/* Company Role */}
            <div className="bg-white rounded-xl p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-building-2-line text-[#1a2e4a]" />
                </div>
                <span className="text-sm font-bold text-[#1a2e4a]">貴社の役割</span>
              </div>
              <ul className="space-y-2">
                {companyTasks.map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#1a2e4a] mt-1.5 flex-shrink-0" />
                    <span className="text-xs text-[#555] leading-relaxed">{t}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Arrow */}
            <div className="flex justify-center items-center py-2 md:py-0">
              <div className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 flex items-center justify-center bg-[#E8601C] rounded-full">
                  <i className="ri-arrow-left-right-line text-white text-base" />
                </div>
                <span className="text-xs text-[#999]">連携</span>
              </div>
            </div>

            {/* Agency Role */}
            <div className="bg-[#1a2e4a] rounded-xl p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-team-line text-white/80" />
                </div>
                <span className="text-sm font-bold text-white">当社の役割</span>
              </div>
              <ul className="space-y-2">
                {agencyTasks.slice(0, 4).map((t) => (
                  <li key={t} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#E8601C] mt-1.5 flex-shrink-0" />
                    <span className="text-xs text-white/70 leading-relaxed">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
